import React, { useEffect, useState } from 'react';
import { DAY_NAMES, DAY_NAMES_SHORT, generateSlots, getDateStr, getMonday, isCampaignActive } from './clientUtils';
import { NEUTRAL, STATUS_COLORS, onAccent } from './designTokens';
import Badge from './components/primitives/Badge';
import { buildEvent, downloadIcs, googleCalendarUrl } from './calendarExport';
import { CONSENT_TEXT } from './consent';

const MONTHS_SHORT = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

const sectionLabel = {
  margin: '0 0 10px', fontSize: '0.75em', color: NEUTRAL.muted, fontWeight: '600',
  textTransform: 'uppercase', letterSpacing: '0.06em',
};

// ISO-veckonummer (svensk standard)
function isoWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

const nowHHMM = () => {
  const n = new Date();
  return String(n.getHours()).padStart(2, '0') + ':' + String(n.getMinutes()).padStart(2, '0');
};

const shortDate = (dateStr) => {
  const d = new Date(dateStr + 'T00:00:00');
  return `${DAY_NAMES_SHORT[d.getDay()]} ${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
};

function priceOf(service) {
  if (!service || service.price == null || service.price === '') return null;
  const campaign = isCampaignActive(service);
  return { now: campaign ? service.campaign_price : service.price, was: campaign ? service.price : null, currency: service.currency || 'kr' };
}

const fmtPrice = (p) => p ? `${p.now} ${p.currency === 'SEK' ? 'kr' : p.currency}` : null;

function CalendarButtons({ event, accent }) {
  const [open, setOpen] = useState(false);
  const pill = {
    display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.85em', fontWeight: '600',
    color: NEUTRAL.ink, padding: '9px 14px', borderRadius: '999px', border: `1px solid ${NEUTRAL.line}`,
    background: 'white', cursor: 'pointer', textDecoration: 'none', fontFamily: 'inherit',
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} style={pill}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="18" rx="3"/><path d="M16 2v4M8 2v4M3 10h18M12 14v4M10 16h4"/>
        </svg>
        Lägg till i kalendern
      </button>
    );
  }
  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
      <button type="button" onClick={() => downloadIcs(event)} style={{ ...pill, borderColor: accent }}>
        iPhone / Outlook
      </button>
      <a href={googleCalendarUrl(event)} target="_blank" rel="noopener noreferrer" style={{ ...pill, borderColor: accent }}>
        Google Kalender
      </a>
    </div>
  );
}

function ClientBookingsTab({
  client,
  bookings,
  bookedSlots,
  cancelledBookings = [],
  schedules,
  blockedTimes,
  services,
  selectedService,
  setSelectedService,
  selectedWeekStart,
  setSelectedWeekStart,
  selectedBookingDate,
  setSelectedBookingDate,
  confirmingSlot,
  setConfirmingSlot,
  bookSlot,
  bookingConfirmed,
  setBookingConfirmed,
  bookingNotice,
  setBookingNotice,
  brand,
  bookingsLoading,
  anamnesisTemplate,
  anamnesisResponse,
  anamnesisJustSubmitted,
  anamnesisLoading,
  anamnesisQuestions,
  anamnesisAnswers,
  setAnamnesisAnswers,
  showAnamnesisForm,
  setShowAnamnesisForm,
  anamnesisSubmitting,
  submitAnamnesis,
  anamnesisConsent,
  setAnamnesisConsent,
  cancellingBookingId,
  setCancellingBookingId,
  cancellationFeedback,
  cancelMyBooking,
  activeDuration,
  activeBreak,
  brandAccent,
  brandTint,
  brandTintBorder,
  theme,
}) {
  const [submitting, setSubmitting] = useState(false);
  const accentText = onAccent(brandAccent);
  const today = getDateStr(new Date());
  const thisMonday = getMonday(new Date());

  // Förvälj första behandlingen så att tiderna syns direkt (chips istället för "välj först").
  useEffect(() => {
    if (services.length > 0 && !selectedService) setSelectedService(services[0]);
  }, [services, selectedService, setSelectedService]);

  // Stäng bekräftelsearket med Esc
  useEffect(() => {
    if (!confirmingSlot) return;
    const onKey = (e) => { if (e.key === 'Escape') setConfirmingSlot(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmingSlot, setConfirmingSlot]);

  // Kontrollerar om sloten är bokad av någon — söker i anonym bookedSlots (alla klienter).
  const isSlotTaken = (dateStr, startTime, endTime) => (bookedSlots || []).some(b => {
    if (b.date !== dateStr) return false;
    return startTime < b.end_time?.slice(0, 5) && endTime > b.start_time?.slice(0, 5);
  });

  const isSlotBlocked = (dateStr, slotStart, slotEnd) => {
    const dayBlocked = blockedTimes.filter(bt => bt.date === dateStr);
    if (dayBlocked.some(bt => bt.all_day)) return true;
    return dayBlocked.some(bt => slotStart < bt.end_time?.slice(0, 5) && slotEnd > bt.start_time?.slice(0, 5));
  };

  const freeSlotsFor = (date) => {
    const dateStr = getDateStr(date);
    if (dateStr < today) return [];
    const schedule = schedules.find(s => s.day_of_week === date.getDay());
    if (!schedule) return [];
    const now = nowHHMM();
    return generateSlots(schedule, activeDuration, activeBreak).filter(s =>
      !isSlotTaken(dateStr, s.start, s.end) &&
      !isSlotBlocked(dateStr, s.start, s.end) &&
      !(dateStr === today && s.start < now)
    );
  };

  const changeWeek = (delta) => {
    const d = new Date(selectedWeekStart);
    d.setDate(d.getDate() + delta * 7);
    if (d < thisMonday) return;
    setSelectedWeekStart(d);
    setSelectedBookingDate(null);
    if (setBookingNotice) setBookingNotice(null);
  };

  const formatBookingDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return `${DAY_NAMES[d.getDay()]} ${d.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long' })}`;
  };

  if (bookingsLoading) {
    return <div style={{ textAlign: 'center', padding: '48px', color: NEUTRAL.muted, fontSize: '0.9em' }}>Laddar...</div>;
  }

  const myBookings = bookings
    .filter(b => b.client_id === client.id)
    .sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time));
  const myCancelledBookings = cancelledBookings
    .filter(b => b.client_id === client.id)
    .sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time));

  const hasScheduleOrBookings = schedules.length > 0 || myBookings.length > 0 || myCancelledBookings.length > 0;

  if (!hasScheduleOrBookings) {
    return (
      <div style={{ textAlign: 'center', padding: '56px 24px 64px' }}>
        <div style={{ width: '56px', height: '56px', background: brandTint, borderRadius: '16px', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={brandAccent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <path d="M16 2v4M8 2v4M3 10h18"/>
            <circle cx="12" cy="16" r="1.5" fill={brandAccent} stroke="none"/>
          </svg>
        </div>
        <p style={{ color: NEUTRAL.ink, margin: '0 0 8px', fontWeight: '600', fontSize: '1.05em' }}>
          Bokningsbar tid kommer snart
        </p>
        <p style={{ color: NEUTRAL.muted, margin: '0 0 6px', fontSize: '0.9em', lineHeight: '1.6', maxWidth: '280px', marginLeft: 'auto', marginRight: 'auto' }}>
          Här kommer du kunna boka tider direkt hos din terapeut. Det dyker upp här så snart din terapeut öppnar sin kalender.
        </p>
        <p style={{ color: NEUTRAL.faint, margin: 0, fontSize: '0.82em', lineHeight: '1.5' }}>
          Har du frågor? Skicka ett meddelande till din terapeut.
        </p>
      </div>
    );
  }

  const upcomingBookings = myBookings.filter(b => b.date >= today);
  const pastBookings = [
    ...myBookings.filter(b => b.date < today),
    ...myCancelledBookings,
  ].sort((a, b) => a.date.localeCompare(b.date) || a.start_time.localeCompare(b.start_time));
  const nextBookingId = upcomingBookings.length > 0 ? upcomingBookings[0].id : null;

  // ── Veckoremsa ──
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(selectedWeekStart);
    d.setDate(d.getDate() + i);
    return { date: d, dateStr: getDateStr(d), free: freeSlotsFor(d) };
  });
  const selectedStr = selectedBookingDate ? getDateStr(selectedBookingDate) : null;
  const activeDay = weekDays.find(d => d.dateStr === selectedStr) || weekDays.find(d => d.free.length > 0) || null;
  const weekEnd = weekDays[6].date;
  const weekLabel = `Vecka ${isoWeek(selectedWeekStart)} · ${MONTHS_SHORT[selectedWeekStart.getMonth()]}${weekEnd.getMonth() !== selectedWeekStart.getMonth() ? '–' + MONTHS_SHORT[weekEnd.getMonth()] : ''}`;
  const canGoBack = selectedWeekStart > thisMonday;

  const svc = selectedService;
  const svcPrice = priceOf(svc);
  const sheetDate = confirmingSlot ? (confirmingSlot.date || selectedStr || activeDay?.dateStr) : null;

  const confirm = async () => {
    if (!confirmingSlot || !sheetDate || submitting) return;
    setSubmitting(true);
    try {
      await bookSlot(sheetDate, confirmingSlot.start, confirmingSlot.end);
    } finally {
      setSubmitting(false);
    }
  };

  const roundBtn = (enabled) => ({
    width: '34px', height: '34px', borderRadius: '999px', border: `1px solid ${NEUTRAL.line}`, background: 'white',
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: enabled ? 'pointer' : 'default',
    opacity: enabled ? 1 : 0.35, color: NEUTRAL.ink, padding: 0,
  });

  const confirmedEvent = bookingConfirmed ? buildEvent({
    id: bookingConfirmed.id,
    date: bookingConfirmed.date,
    startTime: bookingConfirmed.startTime,
    endTime: bookingConfirmed.endTime,
    title: [bookingConfirmed.service?.title, brand?.displayName].filter(Boolean).join(' – ') || 'Bokad tid',
    description: brand?.cancellationInfo || '',
  }) : null;

  return (
    <div>
      {/* Du är bokad! */}
      {bookingConfirmed && (
        <div style={{ background: 'white', border: `1px solid ${NEUTRAL.line}`, borderRadius: '16px', padding: '22px 18px', marginBottom: '20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', textAlign: 'center' }}>
          <span style={{ width: '46px', height: '46px', borderRadius: '999px', background: brandAccent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={accentText} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>
          </span>
          <h2 style={{ margin: '4px 0 0', fontSize: '1.3em', fontWeight: '800', color: NEUTRAL.ink }}>Du är bokad!</h2>
          <span style={{ fontSize: '0.92em', color: NEUTRAL.text }}>
            {shortDate(bookingConfirmed.date)} kl {bookingConfirmed.startTime}{bookingConfirmed.service ? ` · ${bookingConfirmed.service.title}` : ''}
          </span>
          <div style={{ marginTop: '6px' }}>
            <CalendarButtons event={confirmedEvent} accent={brandAccent} />
          </div>
          <button type="button" onClick={() => setBookingConfirmed(null)} style={{ marginTop: '4px', background: 'none', border: 'none', color: NEUTRAL.muted, fontSize: '0.82em', cursor: 'pointer', padding: '6px', fontFamily: 'inherit' }}>
            Stäng
          </button>
        </div>
      )}

      {/* Avbokningsbekräftelse */}
      {cancellationFeedback && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: STATUS_COLORS.success_bg, border: `1px solid ${STATUS_COLORS.success}40`, borderRadius: '10px', padding: '10px 14px', marginBottom: '16px' }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={STATUS_COLORS.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          <span style={{ fontSize: '0.82em', color: STATUS_COLORS.success, fontWeight: '500' }}>Din bokning är avbokad</span>
        </div>
      )}

      {/* Anamnes – inför ditt besök */}
      {!anamnesisLoading && anamnesisTemplate && (
        anamnesisJustSubmitted ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: STATUS_COLORS.success_bg, border: `1px solid ${STATUS_COLORS.success}40`, borderRadius: '10px', padding: '10px 14px', marginBottom: '16px' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={STATUS_COLORS.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span style={{ fontSize: '0.82em', color: STATUS_COLORS.success, fontWeight: '500' }}>Hälsoinformation inför besök är skickad</span>
          </div>
        ) : !anamnesisResponse ? (
          <div style={{ border: `1px solid ${brandTintBorder}`, borderRadius: '12px', padding: '16px 18px', marginBottom: '20px', background: `linear-gradient(${brandTint}, ${brandTint}), #FFFFFF`, position: 'relative' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: showAnamnesisForm ? '14px' : '0' }}>
              <div>
                <p style={{ margin: '0 0 3px', fontWeight: '700', color: NEUTRAL.ink, fontSize: '0.95em' }}>Inför ditt besök</p>
                <p style={{ margin: 0, color: NEUTRAL.muted, fontSize: '0.82em', lineHeight: '1.5' }}>
                  {anamnesisTemplate.intro_text || 'Din terapeut vill veta lite mer om dig inför ditt besök. Det tar bara ett par minuter.'}
                </p>
              </div>
              {!showAnamnesisForm && (
                <button
                  onClick={() => setShowAnamnesisForm(true)}
                  style={{ background: brandAccent, color: onAccent(brandAccent), border: 'none', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.82em', fontWeight: '600', whiteSpace: 'nowrap', marginLeft: '12px', flexShrink: 0 }}
                >
                  Fyll i nu
                </button>
              )}
            </div>

            {showAnamnesisForm && (
              <div>
                {anamnesisQuestions.map((q, index) => (
                  <div key={q.id} style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', fontSize: '0.875em', fontWeight: '600', color: NEUTRAL.ink, marginBottom: '7px' }}>
                      {index + 1}. {q.question_text}
                      {q.is_required && <span style={{ color: STATUS_COLORS.error, marginLeft: '3px' }}>*</span>}
                    </label>
                    {q.question_type === 'yes_no' ? (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {['Ja', 'Nej'].map(opt => (
                          <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '7px 14px', borderRadius: '8px', border: `1px solid ${anamnesisAnswers[q.id] === opt ? brandAccent : NEUTRAL.line}`, background: anamnesisAnswers[q.id] === opt ? 'white' : 'transparent', fontSize: '0.875em', fontWeight: '500', color: anamnesisAnswers[q.id] === opt ? brandAccent : NEUTRAL.muted }}>
                            <input type="radio" name={`q_${q.id}`} value={opt} checked={anamnesisAnswers[q.id] === opt} onChange={() => setAnamnesisAnswers(prev => ({ ...prev, [q.id]: opt }))} style={{ display: 'none' }} />
                            {opt}
                          </label>
                        ))}
                      </div>
                    ) : q.question_type === 'textarea' ? (
                      <textarea
                        value={anamnesisAnswers[q.id] || ''}
                        onChange={(e) => setAnamnesisAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                        placeholder="Skriv ditt svar..."
                        rows={3}
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${NEUTRAL.line}`, fontSize: '0.875em', outline: 'none', resize: 'vertical', boxSizing: 'border-box', lineHeight: '1.5', background: 'white' }}
                      />
                    ) : (
                      <input
                        type="text"
                        value={anamnesisAnswers[q.id] || ''}
                        onChange={(e) => setAnamnesisAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                        placeholder="Skriv ditt svar..."
                        style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: `1px solid ${NEUTRAL.line}`, fontSize: '0.875em', outline: 'none', boxSizing: 'border-box', background: 'white' }}
                      />
                    )}
                  </div>
                ))}
                <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '0.84em', color: NEUTRAL.text, lineHeight: '1.45', cursor: 'pointer', marginTop: '4px' }}>
                  <input
                    type="checkbox"
                    checked={!!anamnesisConsent}
                    onChange={(e) => setAnamnesisConsent(e.target.checked)}
                    style={{ marginTop: '3px', flexShrink: 0 }}
                  />
                  <span>{CONSENT_TEXT}</span>
                </label>
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  <button
                    onClick={submitAnamnesis}
                    disabled={anamnesisSubmitting || !anamnesisConsent}
                    style={{ background: (anamnesisSubmitting || !anamnesisConsent) ? NEUTRAL.muted : brandAccent, color: (anamnesisSubmitting || !anamnesisConsent) ? '#FFFFFF' : onAccent(brandAccent), border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: (anamnesisSubmitting || !anamnesisConsent) ? 'not-allowed' : 'pointer', fontSize: '0.875em', fontWeight: '600' }}
                  >
                    {anamnesisSubmitting ? 'Skickar...' : 'Skicka'}
                  </button>
                  <button
                    onClick={() => setShowAnamnesisForm(false)}
                    style={{ background: 'transparent', color: NEUTRAL.muted, border: `1px solid ${NEUTRAL.line}`, padding: '10px 14px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.875em' }}
                  >
                    Stäng
                  </button>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: STATUS_COLORS.success_bg, border: `1px solid ${STATUS_COLORS.success}40`, borderRadius: '10px', padding: '10px 14px', marginBottom: '16px' }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={STATUS_COLORS.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span style={{ fontSize: '0.82em', color: STATUS_COLORS.success, fontWeight: '500' }}>Hälsoinformation inskickad</span>
          </div>
        )
      )}


      {/* Dina kommande tider */}
      {upcomingBookings.length > 0 && (
        <div style={{ marginBottom: '28px' }}>
          <p style={sectionLabel}>Dina kommande tider</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {upcomingBookings.map(b => {
              const isNext = b.id === nextBookingId;
              const confirmingCancel = cancellingBookingId === b.id;
              const mins = b.service?.duration;
              return (
                <div key={b.id} style={{
                  background: 'white', border: `1px solid ${NEUTRAL.line}`, borderLeft: `3px solid ${brandAccent}`,
                  borderRadius: '12px', padding: '12px 14px',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
                }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontWeight: '700', color: NEUTRAL.ink, fontSize: '0.95em' }}>
                        {shortDate(b.date)} · {b.start_time?.slice(0, 5)}
                      </span>
                      {isNext && <Badge variant="accent" theme={theme}>Nästa</Badge>}
                    </div>
                    <div style={{ fontSize: '0.82em', color: NEUTRAL.muted, marginTop: '2px' }}>
                      {b.service?.title || b.service_title_snapshot || 'Bokad tid'}{mins ? ` · ${mins} min` : ''}
                    </div>
                  </div>
                  {confirmingCancel ? (
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                      <button
                        onClick={() => cancelMyBooking(b.id)}
                        style={{ background: STATUS_COLORS.error_bg, color: STATUS_COLORS.error, border: `1px solid ${STATUS_COLORS.error}40`, padding: '7px 12px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8em', fontWeight: '600', fontFamily: 'inherit' }}
                      >
                        Ja, avboka
                      </button>
                      <button
                        onClick={() => setCancellingBookingId(null)}
                        style={{ background: 'white', color: NEUTRAL.muted, border: `1px solid ${NEUTRAL.line}`, padding: '7px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8em', fontFamily: 'inherit' }}
                      >
                        Behåll
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setCancellingBookingId(b.id)}
                      style={{ background: 'none', border: 'none', color: STATUS_COLORS.error, fontWeight: '600', fontSize: '0.82em', cursor: 'pointer', padding: '6px 4px', fontFamily: 'inherit' }}
                    >
                      Avboka
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {brand?.cancellationInfo && (
            <p style={{ margin: '8px 2px 0', fontSize: '0.78em', color: NEUTRAL.muted }}>{brand.cancellationInfo}</p>
          )}
        </div>
      )}

      {/* Boka tid */}
      {schedules.length > 0 && (
        <div style={{ marginBottom: '28px' }}>
          {/* Behandling */}
          {services.length > 0 && (
            <div style={{ marginBottom: '20px' }}>
              <p style={sectionLabel}>Behandling</p>
              <div className="fr-chip-row" style={{ display: 'flex', gap: '8px', overflowX: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: '2px', scrollbarWidth: 'none' }}>
                {services.map(s => {
                  const active = svc?.id === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => { setSelectedService(s); setSelectedBookingDate(null); if (setBookingNotice) setBookingNotice(null); }}
                      aria-pressed={active}
                      style={{
                        flexShrink: 0, fontSize: '0.88em', fontWeight: active ? '700' : '500', padding: '10px 15px',
                        borderRadius: '999px', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
                        background: active ? NEUTRAL.ink : 'white', color: active ? 'white' : NEUTRAL.ink,
                        border: `1px solid ${active ? NEUTRAL.ink : NEUTRAL.line}`,
                      }}
                    >
                      {s.title}
                    </button>
                  );
                })}
              </div>
              {svc && (
                <p style={{ margin: '8px 2px 0', fontSize: '0.84em', color: NEUTRAL.muted, lineHeight: '1.5' }}>
                  {svc.duration} min
                  {svcPrice && (
                    <>
                      {' · '}
                      {svcPrice.was != null && <span style={{ textDecoration: 'line-through', marginRight: '4px' }}>{svcPrice.was}</span>}
                      <span style={{ color: NEUTRAL.ink, fontWeight: '600' }}>{fmtPrice(svcPrice)}</span>
                    </>
                  )}
                  {svc.description ? ` · ${svc.description}` : ''}
                </p>
              )}
            </div>
          )}

          {/* Veckoremsa */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <p style={{ ...sectionLabel, margin: 0 }}>{weekLabel}</p>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button type="button" aria-label="Föregående vecka" disabled={!canGoBack} onClick={() => changeWeek(-1)} style={roundBtn(canGoBack)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>
              </button>
              <button type="button" aria-label="Nästa vecka" onClick={() => changeWeek(1)} style={roundBtn(true)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>
              </button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: '4px', marginBottom: '18px' }}>
            {weekDays.map(({ date, dateStr, free }) => {
              const available = free.length > 0;
              const selected = activeDay?.dateStr === dateStr;
              const isToday = dateStr === today;
              return (
                <button
                  key={dateStr}
                  type="button"
                  disabled={!available}
                  onClick={() => { setSelectedBookingDate(date); if (setBookingNotice) setBookingNotice(null); }}
                  aria-label={`${DAY_NAMES[date.getDay()]} ${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}${available ? `, ${free.length} lediga tider` : ', inga lediga tider'}`}
                  aria-pressed={selected}
                  style={{
                    textAlign: 'center', padding: '8px 0 7px', borderRadius: '12px', fontFamily: 'inherit',
                    cursor: available ? 'pointer' : 'default',
                    background: selected ? NEUTRAL.ink : available ? 'white' : 'transparent',
                    border: `1px solid ${selected ? NEUTRAL.ink : available ? NEUTRAL.line : 'transparent'}`,
                    color: selected ? 'white' : NEUTRAL.ink,
                    opacity: available || selected ? 1 : 0.4,
                  }}
                >
                  <div style={{ fontSize: '0.66em', letterSpacing: '0.04em', textTransform: 'uppercase', color: selected ? 'rgba(255,255,255,0.8)' : NEUTRAL.muted }}>
                    {DAY_NAMES_SHORT[date.getDay()]}
                  </div>
                  <div style={{ fontFamily: 'var(--font-heading)', fontWeight: '800', fontSize: '1.05em', textDecoration: isToday ? 'underline' : 'none', textUnderlineOffset: '3px' }}>
                    {date.getDate()}
                  </div>
                  <div style={{ width: '6px', height: '6px', borderRadius: '999px', margin: '3px auto 0', background: available ? brandAccent : 'transparent' }} />
                </button>
              );
            })}
          </div>

          {/* Tider för vald dag */}
          {bookingNotice && (
            <div role="status" style={{ background: STATUS_COLORS.warning_bg, border: `1px solid ${STATUS_COLORS.warning}55`, color: STATUS_COLORS.warning, borderRadius: '10px', padding: '10px 14px', fontSize: '0.85em', marginBottom: '12px' }}>
              {bookingNotice}
            </div>
          )}
          {activeDay ? (
            <div>
              <p style={sectionLabel}>
                {DAY_NAMES[activeDay.date.getDay()]} {activeDay.date.getDate()} {activeDay.date.toLocaleDateString('sv-SE', { month: 'long' })}
                {' · '}{activeDay.free.length} ledig{activeDay.free.length === 1 ? '' : 'a'}
              </p>
              {activeDay.free.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>
                  {activeDay.free.map(slot => (
                    <button
                      key={slot.start}
                      type="button"
                      onClick={() => setConfirmingSlot({ start: slot.start, end: slot.end, date: activeDay.dateStr })}
                      style={{
                        textAlign: 'center', fontSize: '1em', fontWeight: '700', padding: '14px 0', borderRadius: '12px',
                        background: 'white', border: `1.5px solid ${brandAccent}`, color: NEUTRAL.ink, cursor: 'pointer',
                        fontFamily: 'inherit', fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {slot.start}
                    </button>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: '0.88em', color: NEUTRAL.muted }}>Inga lediga tider kvar den här dagen.</p>
              )}
              <p style={{ margin: '10px 2px 0', fontSize: '0.78em', color: NEUTRAL.faint }}>Prickarna visar dagar med lediga tider.</p>
            </div>
          ) : (
            <div style={{ background: 'white', border: `1px solid ${NEUTRAL.line}`, borderRadius: '12px', padding: '18px', textAlign: 'center' }}>
              <p style={{ margin: '0 0 10px', fontSize: '0.9em', color: NEUTRAL.text }}>Inga lediga tider den här veckan.</p>
              <button type="button" onClick={() => changeWeek(1)} style={{ background: 'none', border: `1px solid ${NEUTRAL.line}`, borderRadius: '999px', padding: '8px 14px', fontSize: '0.85em', fontWeight: '600', color: NEUTRAL.ink, cursor: 'pointer', fontFamily: 'inherit' }}>
                Visa nästa vecka →
              </button>
            </div>
          )}
        </div>
      )}

      {/* Bokningshistorik */}
      {pastBookings.length > 0 && (
        <div style={{ marginTop: '28px' }}>
          <p style={{ margin: '0 0 10px', fontSize: '0.78em', color: NEUTRAL.faint, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Bokningshistorik
          </p>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {pastBookings.map(b => (
              <div key={b.id} style={{
                background: NEUTRAL.page,
                border: `1px solid ${NEUTRAL.line}`,
                borderRadius: '12px',
                padding: '14px 18px',
                marginBottom: '8px',
                opacity: 0.65,
              }}>
                <div style={{ fontWeight: '700', color: NEUTRAL.muted, fontSize: '0.93em', marginBottom: '3px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {formatBookingDate(b.date)}
                  {b.status === 'cancelled' && (
                    <Badge variant="neutral">Avbokad</Badge>
                  )}
                </div>
                <div style={{ fontSize: '0.875em', color: NEUTRAL.muted }}>
                  kl {b.start_time?.slice(0, 5)}–{b.end_time?.slice(0, 5)}
                  {b.service && <span style={{ marginLeft: '6px', color: NEUTRAL.muted }}>· {b.service.title}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bekräfta – ark som glider upp */}
      {confirmingSlot && sheetDate && (
        <div
          onClick={() => !submitting && setConfirmingSlot(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 100, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', animation: 'frFadeIn 160ms ease-out' }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="fr-confirm-title"
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: '560px', background: 'white', borderRadius: '24px 24px 0 0',
              padding: '10px 20px calc(24px + env(safe-area-inset-bottom))', boxSizing: 'border-box',
              display: 'flex', flexDirection: 'column', gap: '16px', animation: 'frSheetUp 220ms cubic-bezier(.2,.8,.2,1)',
            }}
          >
            <div style={{ width: '40px', height: '5px', borderRadius: '999px', background: NEUTRAL.line, margin: '0 auto' }} />
            <h2 id="fr-confirm-title" style={{ margin: 0, fontSize: '1.35em', fontWeight: '800', color: NEUTRAL.ink }}>Bekräfta din tid</h2>
            <div style={{ border: `1px solid ${NEUTRAL.line}`, borderRadius: '14px', overflow: 'hidden' }}>
              {[
                svc ? ['Behandling', svc.title] : null,
                ['När', `${shortDate(sheetDate)} · ${confirmingSlot.start}–${confirmingSlot.end}`],
                svcPrice ? ['Pris', fmtPrice(svcPrice)] : null,
              ].filter(Boolean).map(([label, value], i, arr) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '12px 14px', fontSize: '0.92em', borderBottom: i < arr.length - 1 ? `1px solid ${NEUTRAL.line}` : 'none' }}>
                  <span style={{ color: NEUTRAL.muted }}>{label}</span>
                  <strong style={{ color: NEUTRAL.ink, textAlign: 'right' }}>{value}</strong>
                </div>
              ))}
            </div>
            {brand?.cancellationInfo && (
              <p style={{ margin: 0, fontSize: '0.82em', color: NEUTRAL.muted, lineHeight: '1.5' }}>{brand.cancellationInfo}</p>
            )}
            <button
              type="button"
              onClick={confirm}
              disabled={submitting}
              style={{ fontSize: '1em', fontWeight: '700', padding: '16px 0', borderRadius: '14px', border: 'none', background: brandAccent, color: accentText, cursor: submitting ? 'default' : 'pointer', opacity: submitting ? 0.7 : 1, fontFamily: 'inherit' }}
            >
              {submitting ? 'Bokar…' : 'Bekräfta bokning'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmingSlot(null)}
              disabled={submitting}
              style={{ background: 'none', border: 'none', fontSize: '0.9em', color: NEUTRAL.muted, cursor: 'pointer', padding: '4px', fontFamily: 'inherit' }}
            >
              Välj en annan tid
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientBookingsTab;
